import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Button,
  Paper,
  Alert,
  CircularProgress
} from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import { redeemReferralCode } from '../services/api';
import Layout from '../components/Layout';

const Referral = () => {
  const { code } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [redeemed, setRedeemed] = useState(false);
  const [error, setError] = useState(null);
  const [referrerName, setReferrerName] = useState(null);

  const handleRedeemCode = useCallback(async () => {
    if (!code) return;

    try {
      setLoading(true);
      setError(null);
      const response = await redeemReferralCode(code);
      setRedeemed(true);
      setReferrerName(response.referrerName);
      sessionStorage.removeItem('pendingReferralCode');
      setTimeout(() => {
        navigate('/dashboard');
      }, 3000);
    } catch (err) {
      console.error('Error redeeming referral code:', err);
      setError(err.response?.data?.error || 'Failed to apply referral code');
    } finally {
      setLoading(false);
    }
  }, [code, navigate]);

  useEffect(() => {
    // Store referral code for after authentication
    if (code) {
      sessionStorage.setItem('pendingReferralCode', code);
    }

    // If user is already authenticated, try to redeem immediately
    if (user && code) {
      handleRedeemCode();
    }
  }, [code, user, handleRedeemCode]);

  const handleGetStarted = () => {
    // The referral code is already stored in sessionStorage
    // The Welcome page should check for it and apply it during signup
    navigate('/');
  };

  if (!user) {
    // User not authenticated - show invitation to sign up
    return (
      <Layout>
        <Container maxWidth="sm" sx={{ py: 8 }}>
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 2 }}>
              You've been invited to Budgit!
            </Typography>
            <Typography variant="body1" sx={{ mb: 4, color: 'text.secondary' }}>
              A friend has shared Budgit with you. Join today to start managing your finances 
              together and get access to premium features.
            </Typography>
            <Button
              variant="contained"
              size="large"
              onClick={handleGetStarted}
              sx={{ mb: 2 }}
            >
              Get Started
            </Button>
            <Typography variant="body2" color="text.secondary">
              Referral code: <strong>{code}</strong>
            </Typography>
          </Paper>
        </Container>
      </Layout>
    );
  }

  // User is authenticated
  return (
    <Layout>
      <Container maxWidth="sm" sx={{ py: 8 }}>
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          {loading && (
            <Box>
              <CircularProgress sx={{ mb: 2 }} />
              <Typography variant="h6">
                Applying referral code...
              </Typography>
            </Box>
          )}

          {redeemed && (
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 2, color: 'success.main' }}>
                🎉 Referral Applied!
              </Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>
                Thank you for joining through {referrerName}'s invitation! 
                You're all set to enjoy Budgit's premium features.
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Redirecting to your dashboard...
              </Typography>
            </Box>
          )}

          {error && (
            <Box>
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
              <Button
                variant="contained"
                onClick={() => navigate('/dashboard')}
              >
                Continue to Dashboard
              </Button>
            </Box>
          )}

          {!loading && !redeemed && !error && (
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 2 }}>
                Apply Referral Code
              </Typography>
              <Typography variant="body1" sx={{ mb: 3 }}>
                You have a referral code: <strong>{code}</strong>
              </Typography>
              <Button
                variant="contained"
                onClick={handleRedeemCode}
                size="large"
              >
                Apply Code
              </Button>
            </Box>
          )}
        </Paper>
      </Container>
    </Layout>
  );
};

export default Referral;